<template>
  <header class="tbar">
    <div class="tbar__inner">
      <!-- 좌: 스택 화면은 뒤로가기, 탭 화면은 브랜드(모바일만) -->
      <button
        v-if="isStack"
        type="button"
        class="tbar__icon-btn tbar__back"
        aria-label="뒤로"
        @click="goBack"
      >
        <el-icon><ArrowLeft /></el-icon>
      </button>
      <div v-else class="tbar__brand only-mobile" aria-hidden="true">i</div>

      <h1 class="tbar__title u-trunc">{{ title }}</h1>
      <span v-if="subtitle" class="tbar__subtitle only-tablet-up u-trunc">{{ subtitle }}</span>

      <div class="tbar__spacer" />

      <!-- 새로 만들기 — 데스크톱/태블릿의 탭 화면에서만.
           모바일은 각 화면의 FAB 가 같은 역할을 한다(엄지 반경 안이라 그쪽이 낫다). -->
      <el-dropdown v-if="!isMobile && !isStack" trigger="click" @command="onCreate">
        <button type="button" class="tbar__create">
          <el-icon><Plus /></el-icon>
          <span>새로 만들기</span>
        </button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="schedule">
              <span class="tbar__swatch" :style="{ background: 'var(--evt-work)' }" /> 일정
            </el-dropdown-item>
            <el-dropdown-item command="task">
              <span class="tbar__swatch" :style="{ background: 'var(--task-in-progress)' }" /> 업무
            </el-dropdown-item>
            <el-dropdown-item v-if="canReport" command="report" divided>
              <el-icon><Document /></el-icon> 업무보고
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>

      <!-- 알림 벨 -->
      <button type="button" class="tbar__icon-btn tbar__bell" :aria-label="bellLabel" @click="notifOpen = true">
        <el-icon><Bell /></el-icon>
        <NavBadge class="tbar__bell-badge" :count="unread" />
      </button>

      <!-- 아바타 메뉴 — 모바일 전용.
           태블릿 이상에서는 좌측 레일 하단이 계정 메뉴를 담당한다. 둘 다 두면
           같은 메뉴가 화면에 두 번 나온다. -->
      <el-dropdown v-if="isMobile" trigger="click" @command="onCommand">
        <button type="button" class="tbar__avatar-btn" aria-label="내 계정">
          <el-avatar :size="30" :src="avatar">{{ initial }}</el-avatar>
        </button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item disabled>
              <span class="tbar__me">{{ user?.nickname || '사용자' }}</span>
            </el-dropdown-item>
            <el-dropdown-item command="profile" divided>
              <el-icon><User /></el-icon> 프로필
            </el-dropdown-item>
            <el-dropdown-item command="logout">
              <el-icon><SwitchButton /></el-icon> 로그아웃
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>

    <NotificationSheet v-model="notifOpen" />
  </header>
</template>

<script>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { pageTitleOverride } from '@/utils/pageTitle'
import { confirmLogout } from '@/utils/logout'
import { ArrowLeft, User, SwitchButton, Bell, Plus, Document } from '@element-plus/icons-vue'
import NavBadge from './NavBadge.vue'
import NotificationSheet from './NotificationSheet.vue'
import { getProfileImageUrl, getProfileInitial } from '@/utils/profileUtils'
import { NAV_ITEMS } from './navItems'
import { useSheetRoute } from '@/composables/useSheetRoute'
import { useBreakpoint } from '@/composables/useBreakpoint'

export default {
  name: 'TopBar',
  components: { ArrowLeft, User, SwitchButton, Bell, Plus, Document, NavBadge, NotificationSheet },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const { isMobile } = useBreakpoint()

    // 뒤로가기로 알림함이 닫히도록 URL 에 붙인다
    const { isOpen: notifOpen } = useSheetRoute('notif')
    const unread = computed(() => store.getters.notificationUnread || 0)
    const bellLabel = computed(() => (unread.value ? `알림 ${unread.value}건` : '알림'))

    const user = computed(() => store.state.user)
    const avatar = computed(() => getProfileImageUrl(user.value))
    const initial = computed(() => getProfileInitial(user.value))
    const canReport = computed(() => store.getters.canReport)

    const isStack = computed(() => route.meta?.shell === 'stack')

    const title = computed(() => {
      const o = pageTitleOverride.value
      if (o.title && o.path === route.path) return o.title
      if (route.meta?.title) return route.meta.title
      const item = NAV_ITEMS.find((i) => i.key === route.meta?.tab)
      return item ? item.label : '이루다 WORK'
    })

    const subtitle = computed(() => route.meta?.subtitle || '')

    function goBack() {
      // 앱 안에서 쌓은 이전 화면이 있을 때만 back 한다. window.history.length 로 보면 카톡 링크로 새 탭에서
      // 들어온 경우에도 1 보다 커서(그 탭의 앞 페이지) 뒤로가기가 앱 밖으로 나가 버린다.
      // vue-router 가 history.state.back 에 앱 안의 이전 경로를 적어 둔다(useSheetRoute 와 같은 기준).
      if (window.history.state && window.history.state.back) {
        router.back()
        return
      }
      const item = NAV_ITEMS.find((i) => i.key === route.meta?.tab)
      // 이미 그 경로에 있으면 vue-router 가 reject 한다. 무시해도 되는 오류다.
      router.replace(item ? item.to : '/today').catch(() => {})
    }

    /**
     * 새로 만들기 — 화면을 옮기고 쿼리로 작성 다이얼로그를 연다.
     * 여기서 직접 다이얼로그를 띄우지 않는 이유: 일정/업무 작성 폼은
     * 해당 화면이 이미 갖고 있고(저장 후 목록 갱신까지), 상단바가 그걸 또
     * 들고 있으면 저장 후 어느 화면을 다시 그려야 하는지 알 수 없다.
     * `?compose=` 는 이 코드베이스의 `?sheet=` 규약과 같은 방식이다.
     */
    function onCreate(kind) {
      if (kind === 'report') {
        router.push({ name: 'ReportCompose', query: { type: 'daily' } }).catch(() => {})
        return
      }
      if (kind === 'task') {
        router.push({ path: '/tasks', query: { ...(route.path === '/tasks' ? route.query : {}), compose: 'task' } }).catch(() => {})
        return
      }
      const onCalendar = route.path.startsWith('/calendar')
      const query = onCalendar
        ? { ...route.query, compose: 'schedule' }
        : { view: 'month', compose: 'schedule' }
      router.push({ path: onCalendar ? route.path : '/calendar/month', query }).catch(() => {})
    }

    function onCommand(cmd) {
      if (cmd === 'profile') {
        router.push('/profile').catch(() => {})
        return
      }
      if (cmd === 'logout') confirmLogout(store)
    }

    return {
      user, avatar, initial, isStack, isMobile, canReport,
      title, subtitle, goBack, onCommand, onCreate,
      notifOpen, unread, bellLabel
    }
  }
}
</script>

<style scoped>
.tbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--z-topbar);
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  padding-top: var(--safe-top);
  padding-left: var(--safe-left);
  padding-right: var(--safe-right);
}

.tbar__inner {
  height: var(--topbar-h);
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  padding: 0 var(--sp-3) 0 var(--sp-4);
}

.tbar__icon-btn,
.tbar__avatar-btn {
  width: var(--tap-min);
  height: var(--tap-min);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: none;
  border-radius: var(--r-full);
  color: var(--color-text-muted);
  cursor: pointer;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
}

.tbar__icon-btn { font-size: var(--ic-lg); }
.tbar__icon-btn:hover { background: var(--color-surface-2); color: var(--color-text); }
.tbar__icon-btn:active { background: var(--color-border); }

/* 뒤로가기만 좌측 여백을 상쇄한다(벨에는 적용되지 않게) */
.tbar__back { margin-left: calc(var(--sp-3) * -1); }

.tbar__brand {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: var(--r-md);
  background: var(--color-primary);
  color: var(--color-on-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-md);
  font-weight: var(--fw-bold);
}

.tbar__title {
  font-size: var(--fs-lg);
  font-weight: var(--fw-semibold);
  letter-spacing: -0.01em;
  color: var(--color-text);
  margin: 0;
}

.tbar__subtitle {
  font-size: var(--fs-md);
  color: var(--color-text-muted);
  padding-left: var(--sp-1);
}

.tbar__spacer { flex: 1; min-width: 0; }

/* ---------- 새로 만들기 ---------- */
.tbar__create {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border: 0;
  border-radius: var(--r-md);
  background: var(--color-primary);
  color: var(--color-on-accent);
  font-family: inherit;
  font-size: var(--fs-md);
  font-weight: var(--fw-medium);
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--dur-fast) var(--ease);
}
.tbar__create:hover { background: var(--color-primary-hover); }

.tbar__swatch {
  width: 9px;
  height: 9px;
  border-radius: var(--r-xs);
  display: inline-block;
  margin-right: 2px;
}

.tbar__bell { position: relative; }

.tbar__bell-badge {
  position: absolute;
  top: 5px;
  right: 3px;
}

.tbar__me {
  font-weight: var(--fw-semibold);
  color: var(--color-text);
}

/* 태블릿 이상: 사이드바가 브랜드를 담당하므로 상단바는 사이드바 오른쪽에서 시작한다 */
@media (min-width: 768px) { /* --bp-mobile-max + 1 */
  .tbar { left: var(--nav-side-w-collapsed); }
  .tbar__inner { padding: 0 var(--sp-5); }
}

@media (min-width: 1024px) { /* --bp-tablet-max + 1 */
  .tbar { left: var(--nav-side-w); }
}
</style>
